import {
  Component,
  ElementRef,
  Inject,
  InjectionToken,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { AutocompletingSelectMultipleComponent } from '@fullyops/shared/components/autocompleting-select-multiple/component';
import { AutocompletingSelectComponent } from '@fullyops/shared/components/autocompleting-select/component';
import { MaterialModule } from '@fullyops/shared/material.module';
import { I18NextModule } from 'angular-i18next';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  PartsSelectorComponent,
  CountedPartChoice,
  PartChoice,
} from '../../../../shared/components/parts-selector/parts-selector.component';
import { Part, UncataloguedPart } from '@fullyops/data/parts/parts';
import {
  WorkOrderAction,
  WorkOrderActionInput,
} from '@fullyops/data/work-order-actions/work-order-action';
import { MomentTimezoneToAngularTimezonePipe } from '@fullyops/shared/pipes/moment-timezone-to-angular-timezone.pipe';
import { PlusButtonComponent } from '@fullyops/shared/components/plus-button/plus-button.component';
import { ArrowComponent } from '@fullyops/shared/components/arrow/component';
import {
  Attachment,
  ALL_FILE_TYPES,
  FileUploadComponent,
} from '@fullyops/shared/components/file-upload/component';
import { v4 as getUuid } from 'uuid';
import { ToastService } from '@fullyops/shared/services/toast/toast.service';
import { MatDialog } from '@angular/material/dialog';
import {
  KebabMenuComponent,
  KebabMenuItem,
} from '@fullyops/shared/components/kebab-menu/kebab-menu.component';
import {
  RequestPartConfirmationDialogComponent,
  RequestPartConfirmationDialogResult,
} from '@fullyops/features/work-orders/containers/request-part-confirmation-dialog/request-part-confirmation-dialog.component';
import { mapmap } from '@fullyops/shared/observable-pipes';
import { mapAssetToOption } from '@fullyops/shared/asset-option';
import { normalizeForSearch } from '@fullyops/shared/normalize-for-search';
import { AttachmentSet } from '@fullyops/shared/attachment-set';
import { HideIfNotAllowedDirective } from '@fullyops/shared/directives/hide-if-not-allowed.directive';
import { PermissionType } from '@fullyops/legacy/ui/ui-shared/utils/crm-types';
import { MiniAsset } from '@fullyops/data/assets/asset';

export interface ActionFormInputs {
  initialValue: WorkOrderAction | null;
  equipmentRequired: boolean;
  showUncataloguedParts: boolean;
  showAnomalies: boolean;
  canChangeEquipment: boolean;
  equipmentChoices$: BehaviorSubject<MiniAsset[]>;
  /**
   * Translation key saying which machines the picker can list, built by the caller
   * with the helper in shared/assets-tooltip.
   */
  equipmentsTooltipKey: string;
  anomalyChoices$: Observable<{ id: string; name: string }[]>;
  partChoices$: Observable<Part[]>;
  uncataloguedPartChoices$: Observable<UncataloguedPart[]>;
  onCancel: () => void;
  onSave: (action: WorkOrderActionInput, newAttachments: File[]) => void;
  onDelete: () => void;
  onRequestPart: () => void;
  onCreateEquipment: (() => Observable<MiniAsset>) | null;
  onCreateAnomaly: (description: string) => Observable<string>;
  onCreateUncataloguedPart: (name: string) => Observable<string>;
}

export const ACTION_FORM_TOKEN = new InjectionToken<ActionFormInputs>(
  'action-form-inputs',
);

@Component({
  selector: 'fo-action-form',
  templateUrl: './action-form.component.html',
  styleUrl: './action-form.component.scss',
  imports: [
    ReactiveFormsModule,
    MaterialModule,
    I18NextModule,
    AutocompletingSelectComponent,
    AutocompletingSelectMultipleComponent,
    PartsSelectorComponent,
    PlusButtonComponent,
    FileUploadComponent,
    ArrowComponent,
    MomentTimezoneToAngularTimezonePipe,
    KebabMenuComponent,
    HideIfNotAllowedDirective,
  ],
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
  ],
})
export class ActionFormComponent implements OnInit {
  protected readonly acceptedFileTypes = ALL_FILE_TYPES;
  protected deletePermissions = [PermissionType.CAN_DELETE_INTERVENTIONS];

  protected kebabItems: KebabMenuItem[] = [
    {
      label: 'delete',
      highlight: 'warn',
      action: () => this.inputs.onDelete(),
    },
  ];

  private initialAttachmentIds = new Set<string>();

  constructor(
    @Inject(ACTION_FORM_TOKEN) protected inputs: ActionFormInputs,
    private toastService: ToastService,
    private hostElement: ElementRef,
    private dialog: MatDialog,
  ) {}

  ngOnInit() {
    const v = this.inputs.initialValue;
    if (v != null) {
      this.actionForm.setValue({
        title: v.title,
        description: v.description,
        internalObservation: v.internalObservation,
        userObservation: v.userObservation,
        equipment: v.equipment?.equipment ?? null,
        anomalies: v.anomalies.map((a) => ({ id: a.id, name: a.description })),
        parts: v.parts,
        uncataloguedParts: v.uncataloguedParts,
      });
      this.attachmentSet.displayedAttachments = (v.attachments ?? []).map(
        (as) => ({
          id: as.id,
          label: as.fileName,
          pending: false,
          file: { fileName: as.fileName, signedUrl: as.url },
        }),
      );

      this.initialAttachmentIds = new Set(
        v.attachments?.map((a) => a.id) ?? [],
      );
    }
    if (!this.inputs.canChangeEquipment) {
      this.actionForm.controls.equipment.disable({ emitEvent: false });
    }
  }

  protected attachmentSet = new AttachmentSet();

  protected equipmentsDisplaySelectedOptionLabel(v: MiniAsset): string {
    if (v == null) {
      return '';
    }

    return v.serialNumber ? `[${v.serialNumber}] ${v.name}` : v.name;
  }

  protected equipmentsMatchPredicate(v: MiniAsset, pattern: string): boolean {
    return (
      normalizeForSearch(v.name).indexOf(pattern) !== -1 ||
      normalizeForSearch(v.serialNumber).indexOf(pattern) !== -1
    );
  }

  protected partsMatchPredicate(v: PartChoice, pattern: string): boolean {
    return (
      normalizeForSearch(v.name).indexOf(pattern) !== -1 ||
      normalizeForSearch(v.ref).indexOf(pattern) !== -1
    );
  }

  protected actionForm = new FormGroup({
    title: new FormControl('', {
      nonNullable: true,
      validators: Validators.required,
    }),
    equipment: new FormControl<MiniAsset>(
      null,
      this.inputs.equipmentRequired ? Validators.required : [],
    ),
    anomalies: new FormControl<{ id: string; name: string }[]>([], {
      nonNullable: true,
    }),
    parts: new FormControl<CountedPartChoice[]>([], { nonNullable: true }),
    uncataloguedParts: new FormControl<CountedPartChoice[]>([], {
      nonNullable: true,
    }),
    description: new FormControl<string>('', {
      nonNullable: true,
      validators: Validators.required,
    }),
    internalObservation: new FormControl<string>('', { nonNullable: true }),
    userObservation: new FormControl<string>('', { nonNullable: true }),
  });

  protected equipmentChoices$ = this.inputs.equipmentChoices$.pipe(
    mapmap(mapAssetToOption),
  );

  protected onCreateEquipment() {
    this.inputs.onCreateEquipment().subscribe((equipment) => {
      if (equipment != null) {
        this.actionForm.controls.equipment.setValue(equipment);
      }
    });
  }

  // can't use static: true because the field is included conditionally
  @ViewChild('anomalyField')
  private anomalyAutocomplete: AutocompletingSelectMultipleComponent<{
    id: string;
    name: string;
  }>;

  @ViewChild('uncataloguedParts')
  private uncataloguedPartsField: PartsSelectorComponent;

  protected createAnomaly(description: string) {
    if (!description || description.trim().length === 0) {
      return;
    }

    this.inputs.onCreateAnomaly(description).subscribe((anomalyId) => {
      const ctl = this.actionForm.controls.anomalies;
      ctl.setValue([...ctl.value, { id: anomalyId, name: description }]);
      this.anomalyAutocomplete.literalText = '';
    });
  }

  protected onCreateAnomaly() {
    const description = this.anomalyAutocomplete.literalText;
    this.createAnomaly(description);
  }

  protected onCreateUncataloguedPart(name: string) {
    this.inputs.onCreateUncataloguedPart(name).subscribe((partId) => {
      const ctl = this.actionForm.controls.uncataloguedParts;
      ctl.setValue([...ctl.value, { part: { id: partId, name }, quantity: 1 }]);
      this.uncataloguedPartsField.literalText = '';
    });
  }

  private hasUnsavedChanges(): boolean {
    const isNewAction = this.inputs.initialValue == null;

    if (
      this.actionForm.dirty ||
      this.attachmentSet.pendingAttachments.length > 0
    ) {
      return true;
    }

    if (isNewAction) {
      return this.attachmentSet.displayedAttachments.length > 0;
    }

    const currentAttachmentIds = new Set(
      this.attachmentSet.displayedAttachments
        .filter((a) => !a.pending)
        .map((a) => a.id),
    );

    if (currentAttachmentIds.size !== this.initialAttachmentIds.size) {
      return true;
    }

    for (const id of this.initialAttachmentIds) {
      if (!currentAttachmentIds.has(id)) {
        return true;
      }
    }

    return false;
  }

  private extractFormData(): WorkOrderActionInput {
    const v = this.actionForm.value;
    return {
      title: v.title,
      description: v.description,
      internalObservation: v.internalObservation,
      userObservation: v.userObservation,
      equipmentId: v.equipment?.id,
      anomalyIds: v.anomalies.map((a) => a.id),
      parts: v.parts.map((p) => ({
        partId: p.part.id,
        quantity: p.quantity,
      })),
      uncataloguedParts: v.uncataloguedParts.map((p) => ({
        partId: p.part.id,
        quantity: p.quantity,
      })),
      attachmentIds: this.attachmentSet.displayedAttachments
        .filter((a) => !a.pending)
        .map((a) => a.id),
    };
  }

  protected onSave() {
    if (!this.actionForm.valid) {
      this.scrollToFirstInvalidControl();
      this.toastService.showInvalidFieldsFormErrorMessage();
      return;
    }

    this.inputs.onSave(
      this.extractFormData(),
      this.attachmentSet.pendingAttachments,
    );
  }

  private scrollToFirstInvalidControl() {
    const invalidFieldName = this.firstInvalidControlName();
    if (invalidFieldName == null) {
      return;
    }
    const invalidFieldElement = (
      this.hostElement.nativeElement as HTMLElement
    ).querySelector(`[formcontrolname="${invalidFieldName}"]`);
    if (invalidFieldElement == null) {
      return;
    }
    invalidFieldElement.scrollIntoView({ behavior: 'smooth' });
    if (invalidFieldElement instanceof HTMLInputElement) {
      invalidFieldElement.focus();
    }
  }

  private firstInvalidControlName(): string | null {
    for (const name in this.actionForm.controls) {
      if (this.actionForm.controls[name].invalid) {
        return name;
      }
    }
    return null;
  }

  protected onEnterKey(event: Event) {
    event.preventDefault();
  }

  protected onRequestPart() {
    if (!this.hasUnsavedChanges()) {
      this.inputs.onRequestPart();
      return;
    }

    this.dialog
      .open<
        RequestPartConfirmationDialogComponent,
        never,
        RequestPartConfirmationDialogResult
      >(RequestPartConfirmationDialogComponent)
      .afterClosed()
      .subscribe((result) => {
        if (result === 'saveAndContinue') {
          this.onSave();
          if (this.actionForm.valid) {
            this.inputs.onRequestPart();
          }
        }
      });
  }
}

interface DisplayedAttachment extends Attachment {
  pending: boolean;
}

function previewOfPendingAttachment(f: File): DisplayedAttachment {
  const signedUrl = URL.createObjectURL(f);
  return {
    id: getUuid(),
    label: f.name,
    pending: true,
    file: {
      fileName: f.name,
      signedUrl,
    },
  };
}
